import React, { useEffect, useState, memo, useCallback } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

const AboutAdmin = memo(() => {
    const { t, i18n } = useTranslation();
    const [aboutUs, setAboutUs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);

    const [form, setForm] = useState({
        suptitle: "", suptitle_ar: "",
        title: "", title_ar: "",
        description: "", description_ar: "",
        value: "", value_ar: "",
        purpose: "", purpose_ar: "",
        mission: "", mission_ar: "",
        experience_text: "", experience_text_ar: "",
        experience_years: "",
        image_url: "",
        icon: "",
        is_active: 1
    });
    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState("");
    const navigate = useNavigate();

    const isArabic = i18n.language?.startsWith('ar');

    const getHeaders = useCallback(() => {
        const token = getToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, []);

    const fetchAboutUs = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/about-us`, { headers: getHeaders() });
            setAboutUs(res.data || []);
        } catch (err) {
            console.error(err);
        } finally { setLoading(false); }
    }, [getHeaders]);

    useEffect(() => { fetchAboutUs(); }, [fetchAboutUs]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const data = new FormData();
        Object.keys(form).forEach(key => {
            if (key === 'experience_years') {
                data.append(key, form[key] ? Number(form[key]) : 0);
            } else {
                data.append(key, form[key]);
            }
        });
        if (imageFile) data.append("image", imageFile);

        try {
            if (editing) {
                await axios.put(`${CONFIG.API_URL}/api/about-us/${editing}`, data, { headers: getHeaders() });
            } else {
                await axios.post(`${CONFIG.API_URL}/api/about-us`, data, { headers: getHeaders() });
            }
            setEditing(null);
            resetForm();
            fetchAboutUs();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || t("admin.about_admin.save_failed", "Failed to save About Us entry"));
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setForm({
            suptitle: "", suptitle_ar: "",
            title: "", title_ar: "",
            description: "", description_ar: "",
            value: "", value_ar: "",
            purpose: "", purpose_ar: "",
            mission: "", mission_ar: "",
            experience_text: "", experience_text_ar: "",
            experience_years: "",
            image_url: "",
            icon: "",
            is_active: 1
        });
        setImageFile(null);
        setPreview("");
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t("admin.about_admin.delete_confirm"))) return;
        try {
            await axios.delete(`${CONFIG.API_URL}/api/about-us/${id}`, { headers: getHeaders() });
            fetchAboutUs();
        } catch (err) {
            alert(t("admin.about_admin.delete_failed"));
        }
    };

    const startEdit = (about) => {
        setEditing(about.id);
        setForm({
            suptitle: about.suptitle, suptitle_ar: about.suptitle_ar,
            title: about.title, title_ar: about.title_ar,
            description: about.description, description_ar: about.description_ar,
            value: about.value, value_ar: about.value_ar,
            purpose: about.purpose, purpose_ar: about.purpose_ar,
            mission: about.mission, mission_ar: about.mission_ar,
            experience_text: about.experience_text, experience_text_ar: about.experience_text_ar,
            experience_years: about.experience_years,
            image_url: about.image_url,
            is_active: about.is_active,
            icon: about.icon
        });
        setPreview(about.image_url);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const [search, setSearch] = useState("");

    const filteredAboutUs = aboutUs.filter(a =>
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        a.description.toLowerCase().includes(search.toLowerCase()) ||
        a.id.toString().includes(search)
    );

    return (
        <div className="max-w-6xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            <div className="flex flex-wrap justify-center md:justify-between items-center gap-4 md:gap-0 mb-12">
                <div>
                    <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t("admin.about_admin.title")}</h2>
                    <p className="text-gray-500 mt-2 font-medium">{t("admin.about_admin.subtitle")}</p>
                </div>
                <div className="flex gap-4">
                    <button onClick={() => navigate("/admin")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                        <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="mb-16 bg-brand-primary/5 p-10 rounded-[40px] border border-brand-primary/10">
                <h3 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
                    <span className="w-8 h-8 bg-brand-primary text-white rounded-xl flex items-center justify-center text-sm">
                        <i className={`fas ${editing ? "fa-edit" : "fa-plus"}`}></i>
                    </span>
                    {isArabic ? (editing ? t("admin.about_admin.update_about_ar")
                        : t("admin.about_admin.create_about_ar")
                    ) :
                        (editing
                            ? t("admin.about_admin.update_about_en")
                            : t("admin.about_admin.create_about_en")
                        )
                    }
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.suptitle_en")}</label>
                            <input value={form.suptitle} onChange={e => setForm({ ...form, suptitle: e.target.value })} placeholder={t("admin.about_admin.placeholders.suptitle_en")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold" required />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.title_en")}</label>
                            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={t("admin.about_admin.placeholders.title_en")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold" required />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.description_en")}</label>
                            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={t("admin.about_admin.placeholders.description_en")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.value_en")}</label>
                            <textarea value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder={t("admin.about_admin.placeholders.value_en")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.purpose_en")}</label>
                            <textarea value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder={t("admin.about_admin.placeholders.purpose_en")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.mission_en")}</label>
                            <textarea value={form.mission} onChange={e => setForm({ ...form, mission: e.target.value })} placeholder={t("admin.about_admin.placeholders.mission_en")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.experience_text_en")}</label>
                            <textarea value={form.experience_text} onChange={e => setForm({ ...form, experience_text: e.target.value })} placeholder={t("admin.about_admin.placeholders.experience_text_en")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.experience_years")}</label>
                            <input type="number" value={form.experience_years} onChange={e => setForm({ ...form, experience_years: e.target.value })} placeholder={t("admin.about_admin.placeholders.experience_years")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.category_admin.table_icon")}</label>
                            <input value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} placeholder={t("admin.about_admin.icon_placeholder", "e.g. fas fa-history")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium" />
                        </div>
                    </div>
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.suptitle_ar")}</label>
                            <input value={form.suptitle_ar} onChange={e => setForm({ ...form, suptitle_ar: e.target.value })} dir="rtl" placeholder={isArabic && t("admin.about_admin.placeholders.suptitle_ar")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.title_ar")}</label>
                            <input value={form.title_ar} onChange={e => setForm({ ...form, title_ar: e.target.value })} dir="rtl" placeholder={isArabic && t("admin.about_admin.placeholders.title_ar")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.description_ar")}</label>
                            <textarea value={form.description_ar} onChange={e => setForm({ ...form, description_ar: e.target.value })} dir="rtl" placeholder={isArabic && t("admin.about_admin.placeholders.description_ar")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.value_ar")}</label>
                            <textarea value={form.value_ar} onChange={e => setForm({ ...form, value_ar: e.target.value })} dir="rtl" placeholder={isArabic && t("admin.about_admin.placeholders.value_ar")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.purpose_ar")}</label>
                            <textarea value={form.purpose_ar} onChange={e => setForm({ ...form, purpose_ar: e.target.value })} dir="rtl" placeholder={isArabic && t("admin.about_admin.placeholders.purpose_ar")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.mission_ar")}</label>
                            <textarea value={form.mission_ar} onChange={e => setForm({ ...form, mission_ar: e.target.value })} dir="rtl" placeholder={isArabic && t("admin.about_admin.placeholders.mission_ar")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.about_admin.experience_text_ar")}</label>
                            <textarea value={form.experience_text_ar} onChange={e => setForm({ ...form, experience_text_ar: e.target.value })} dir="rtl" placeholder={isArabic && t("admin.about_admin.placeholders.experience_text_ar")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.bg_image")}</label>

                        <div
                            className="border-2 border-dashed border-brand-primary/20 rounded-[30px] bg-white p-8 text-center cursor-pointer hover:border-brand-primary transition-all group shadow-sm"
                            onClick={() => document.getElementById('hero-image-input').click()}
                        >
                            <input
                                id="hero-image-input"
                                type="file"
                                onChange={handleFileChange}
                                accept="image/*"
                                className="hidden"
                            />
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-12 h-12 bg-brand-primary/5 rounded-2xl flex items-center justify-center text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-all mb-2">
                                    <i className="fas fa-image text-xl"></i>
                                </div>
                                <p className="font-black text-gray-900 uppercase tracking-widest text-[10px] mb-1">
                                    {imageFile ? imageFile.name : t('admin.products_page.form.click_to_browse', 'Click to browse')}
                                </p>
                                {!imageFile && <p className="text-[10px] text-gray-400 font-medium">{t("admin.hero_admin.placeholders.no_file_selected", "No file selected.")}</p>}
                            </div>
                        </div>

                        {preview && (
                            <div className="mt-4 relative h-40 rounded-[30px] overflow-hidden border-2 border-brand-primary/10 shadow-lg">
                                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => { setImageFile(null); setPreview(""); setForm({ ...form, image_url: "" }); }} className="absolute top-3 right-3 bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-xs shadow-xl hover:scale-110 active:scale-90 transition-all">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        )}

                        {!preview && (
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <i className="fas fa-link text-gray-300 text-sm"></i>
                                </div>
                                <input
                                    value={form.image_url}
                                    onChange={e => setForm({ ...form, image_url: e.target.value })}
                                    placeholder={t("admin.hero_admin.placeholders.image_url", "Or paste URL...")}
                                    className="bg-white border-none pl-10 pr-4 py-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium text-sm transition-all"
                                />
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-4 pt-8 ml-4">
                        <div className="flex items-center gap-4">
                            <input type="checkbox" id="isActive" checked={form.is_active === 1} onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} className="w-5 h-5 accent-brand-primary" />
                            <label htmlFor="isActive" className="text-sm font-black text-gray-700 uppercase tracking-widest">{t("admin.about_admin.active_about")}</label>
                        </div>
                    </div>
                </div>

                <div className="mt-12 flex gap-4">
                    <button type="submit" disabled={saving} className="bg-brand-primary text-white px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2">
                        {saving && <i className="fas fa-spinner fa-spin"></i>}
                        {saving ? t("admin.common.saving", "Saving...") : (editing ? t("admin.about_admin.save_changes") : t("admin.about_admin.create_btn"))}
                    </button>
                    {editing && <button type="button" onClick={() => { setEditing(null); resetForm(); }} className="text-gray-500 font-bold px-4">{t("admin.about_admin.cancel")}</button>}
                </div>
            </form >
            <div className="mb-8 flex items-center bg-gray-50 p-4 rounded-[30px] border border-gray-100">

                <div className="relative w-full">
                    <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('admin.common.search_placeholder')}
                        className="pl-12 pr-4 py-3 rounded-2xl border-none bg-gray-50 focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium w-64"
                    />
                </div>
            </div>

            {
                loading ? <div className="text-center py-20">< i className="fas fa-spinner fa-spin text-4xl text-brand-primary" ></i ></div > : (
                    <div className="grid grid-cols-1 gap-8">
                        {filteredAboutUs.map(about => (
                            <div key={about.id} className={`p-8 rounded-[40px] border transition-all ${about.is_active ? 'border-brand-primary bg-brand-primary/5 shadow-xl shadow-brand-primary/10' : 'border-gray-100 bg-white shadow-sm hover:shadow-lg'}`}>
                                <div className="flex flex-col md:flex-row gap-8 items-start">
                                    <div className="w-full md:w-1/3 aspect-video rounded-[30px] overflow-hidden relative group">
                                        <img src={about.image_url} alt={about.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                        <div className="absolute top-4 left-4">
                                            <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest backdrop-blur-md bg-black/50 text-white">
                                                #{about.id}
                                            </span>
                                        </div>
                                        <div className={`absolute top-4 right-4 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest backdrop-blur-md ${about.is_active ? "bg-green-500/80 text-white" : "bg-gray-500/80 text-white"}`}>
                                            {about.is_active ? t("admin.about_admin.active_status") : t("admin.about_admin.inactive_status")}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <p className="text-gray-400 text-xs mt-1 italic line-clamp-1">{about.suptitle}</p>
                                        <h4 className="font-black text-xl text-gray-900">{about.title}</h4>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => startEdit(about)} className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all">
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button onClick={() => handleDelete(about.id)} className="w-10 h-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center hover:bg-red-600 hover:text-white transition-all">
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            }
        </div >
    );
});

export default AboutAdmin;
